import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { IconButton, InputAdornment } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_LETTER_COUNT } from 'pages/ParentHelper/components/Chat/components/Footer/constants';
import { useParentHelperContext } from 'pages/ParentHelper/providers/ParentHelperProvider/hooks';
import { FormOutlinedInput } from 'shared/components/form/components/FormOutlinedInputField';
import { useAppSelector } from 'shared/hooks/store';

import { useInputFieldFocus } from './hooks';

import type { OutlinedInputProps as MuiOutlinedInputProps } from '@mui/material';
import type { FieldsName } from 'pages/ParentHelper/components/Chat/components/Footer/types';
import type { FC, KeyboardEvent } from 'react';

interface OutlinedInputProps extends MuiOutlinedInputProps {
  name: FieldsName;
  onSubmit(): void;
}

export const OutlinedInputField: FC<OutlinedInputProps> = ({
  onSubmit,
  ...props
}) => {
  const { enabled } = useAppSelector(state => state.parentHelper);
  const { t } = useTranslation();

  const { isLoadingAnswer, isLoadingChat, deletedChatId, chatId } =
    useParentHelperContext();

  const { inputFieldRef } = useInputFieldFocus();

  const isDeletedChatSelected =
    !!chatId && !!deletedChatId && chatId === deletedChatId;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        !isLoadingAnswer &&
        event.key === 'Enter' &&
        !event.shiftKey &&
        !isDeletedChatSelected
      ) {
        event.preventDefault();

        onSubmit();
      }
    },
    [onSubmit, isLoadingAnswer, isDeletedChatSelected],
  );

  return (
    <FormOutlinedInput
      disabled={!enabled}
      maxRows="8"
      onKeyDown={handleKeyDown}
      placeholder={t('pages.parentHelper.chat.footer.inputPlaceholder')}
      maxLetterCount={MAX_LETTER_COUNT}
      inputRef={inputFieldRef}
      endAdornment={
        <InputAdornment position="end">
          <IconButton
            type="submit"
            disabled={
              isLoadingChat ||
              isLoadingAnswer ||
              !enabled ||
              isDeletedChatSelected
            }
          >
            <SendOutlinedIcon />
          </IconButton>
        </InputAdornment>
      }
      {...props}
    />
  );
};
